/** Anbieter: Impressum und Datenschutzerklärung pflegen und freigeben (E8). Vor der Freigabe ist nichts öffentlich. */
import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Card, FormGrid, PageHeader } from "../ui/Layout";
import { api, ApiFehler, rufe } from "../api/client";
import { DATENSCHUTZ_ENTWURF, type Impressum } from "@shared/schemas/rechtliches";
import { formatDatumZeit } from "@shared/zeit";
import { TextAbsaetze } from "../seiten/Rechtliches";

interface Daten {
  impressum: Impressum;
  datenschutzText: string;
  freigegebenAt: string | null;
  aktualisiertAt: string | null;
  entwurf: boolean;
}

export function Rechtliches() {
  const q = useQuery({ queryKey: ["admin", "rechtliches"], queryFn: () => rufe<Daten>(api.api.admin.rechtliches.$get()) });
  return (
    <>
      <PageHeader title="Rechtliches" sub="Impressum und Datenschutzerklärung für app.meinregalladen.de" />
      <Seitenzustand query={q} lines={6}>{(d) => <Formular d={d} />}</Seitenzustand>
    </>
  );
}

function Formular({ d }: { d: Daten }) {
  const qc = useQueryClient();
  const [impressum, setImpressum] = useState<Impressum>(d.impressum);
  const [text, setText] = useState(d.datenschutzText);
  const [freigegeben, setFreigegeben] = useState(!!d.freigegebenAt);
  const [vorschau, setVorschau] = useState(false);
  const [erfolg, setErfolg] = useState(false);
  const speichern = useMutation({
    mutationFn: () => rufe(api.api.admin.rechtliches.$put({ json: { impressum, datenschutzText: text, freigegeben } })),
    onSuccess: () => { setErfolg(true); void qc.invalidateQueries({ queryKey: ["admin", "rechtliches"] }); void qc.invalidateQueries({ queryKey: ["public", "rechtliches"] }); },
  });
  const feld = (k: keyof Impressum, label: string, extra: Record<string, unknown> = {}) => <TextField label={label} value={impressum[k]} onChange={(e) => setImpressum({ ...impressum, [k]: e.target.value })} {...extra} />;
  const pflichtFehlt = !impressum.firma.trim() || !impressum.strasse.trim() || !impressum.ort.trim() || !impressum.email.trim();

  function absenden(e: FormEvent) {
    e.preventDefault();
    setErfolg(false);
    speichern.mutate();
  }

  return (
    <form className="app-form" onSubmit={absenden} noValidate>
      {erfolg ? <Notice tone="success">Gespeichert.{freigegeben ? " Impressum und Datenschutz sind öffentlich." : " Noch nicht öffentlich – erst freigeben."}</Notice> : null}
      {speichern.isError ? <Notice tone="error">{speichern.error instanceof ApiFehler ? speichern.error.message : "Speichern hat nicht geklappt."}</Notice> : null}
      {d.entwurf ? <Notice tone="attention" title="Entwurf, juristisch nicht geprüft">Die Datenschutzerklärung ist ein Vorschlag, der zur Technik passt (Cloudflare EU, SumUp, E-Mail-Link, Push, Turnstile). Vor der Freigabe prüfen lassen und Lücken füllen.</Notice> : null}
      <Card title="Impressum" actions={d.freigegebenAt ? <StatusBadge status="paid">Öffentlich seit {formatDatumZeit(d.freigegebenAt)}</StatusBadge> : <StatusBadge status="attention">Nicht öffentlich</StatusBadge>}>
        <FormGrid>
          {feld("firma", "Firma oder Name")}
          {feld("vertretenDurch", "Vertreten durch")}
          {feld("strasse", "Straße und Hausnummer")}
          {feld("plz", "PLZ")}
          {feld("ort", "Ort")}
          {feld("email", "E-Mail", { type: "email" })}
          {feld("telefon", "Telefon")}
          {feld("ustId", "USt-IdNr.")}
          {feld("register", "Registergericht und Nummer")}
          {feld("inhaltlichVerantwortlich", "Inhaltlich verantwortlich (falls abweichend)")}
          {feld("datenschutzbeauftragter", "Datenschutzbeauftragte Person (falls bestellt)")}
        </FormGrid>
      </Card>
      <Card title="Datenschutzerklärung" actions={<><Button variant="quiet" onClick={() => setVorschau(!vorschau)}>{vorschau ? "Bearbeiten" : "Vorschau"}</Button><Button variant="quiet" icon="undo-2" onClick={() => setText(DATENSCHUTZ_ENTWURF)}>Entwurf wiederherstellen</Button></>}>
        {vorschau ? <TextAbsaetze text={text} /> : <textarea className="sc-textarea app-vorlage" aria-label="Datenschutzerklärung" value={text} onChange={(e) => setText(e.target.value)} spellCheck />}
        <p className="sc-muted sc-small">Überschriften mit <span className="app-code"># </span> und <span className="app-code">## </span>, Absätze durch Leerzeilen.</p>
      </Card>
      <Card title="Freigabe">
        <label className="sc-check"><input type="checkbox" checked={freigegeben} onChange={(e) => setFreigegeben(e.target.checked)} disabled={pflichtFehlt} /> Geprüft – Impressum und Datenschutzerklärung veröffentlichen</label>
        {pflichtFehlt ? <p className="sc-muted sc-small">Zum Freigeben fehlen Firma, Straße, Ort oder E-Mail.</p> : null}
      </Card>
      <div className="app-form__actions">
        <Button type="submit" variant="primary" icon="check" loading={speichern.isPending}>Speichern</Button>
      </div>
    </form>
  );
}
